import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'

import { RootState } from '#/app/store/store'
import { api, createChat as createChatApi, getAllChats } from '#/shared/api/endpoints'
import { getApiUrl } from '#/shared/lib/constants'

import { Chat } from './types'

export type PendingFocusMessage = {
	chatUid: string
	messageUid: string
	modelSlug: string
	requestId: number
}

export type ChatsState = {
	chats: Chat[] | null
	currentChat: string | null
	loading: boolean
	error: string | null
	pendingFocus: PendingFocusMessage | null
}

const initialState: ChatsState = {
	chats: null,
	currentChat: null,
	loading: false,
	error: null,
	pendingFocus: null,
}

export const fetchChatsByModel = createAsyncThunk<
	{ chats: Chat[]; currentChat: string | null },
	{ model: string; token?: string }
>('chats/fetchByModel', async ({ model, token }) => {
	const list = await getAllChats(model, token)
	const chats = list ?? []
	const currentChat = chats.length > 0 ? chats[0].uid : null
	console.log(chats)
	return { chats, currentChat }
})

export const createChatForModel = createAsyncThunk<Chat, { model: string; token?: string }>(
	'chats/createForModel',
	async ({ model, token }) => {
		const chat = await createChatApi(model, token)
		if (chat === null) {
			throw new Error('Не удалось создать чат')
		}
		return chat
	}
)

export const deleteChatByUid = createAsyncThunk<string, { uid: string; token?: string }>(
	'chats/deleteByUid',
	async ({ uid, token }) => {
		await axios.delete(getApiUrl() + `/chats/${uid}/`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		return uid
	}
)

export const renameChatByUid = createAsyncThunk<{ uid: string; title: string }, { uid: string; title: string; token?: string }>(
	'chats/renameByUid',
	async ({ uid, title, token }) => {
		const res = await api.updRenameChat(uid, title, token)
		if (res.isError || !res.data) {
			throw new Error('Не удалось переименовать чат')
		}
		return { uid: res.data.uid, title: res.data.title }
	}
)

const chatsSlice = createSlice({
	name: 'chats',
	initialState,
	reducers: {
		setCurrentChat(state, action: PayloadAction<string | null>) {
			state.currentChat = action.payload
		},
		moveChatToTop(state, action: PayloadAction<string>) {
			if (!state.chats) return
			const index = state.chats.findIndex((c) => c.uid === action.payload)
			if (index <= 0) return
			const [item] = state.chats.splice(index, 1)
			state.chats.unshift(item)
		},
		setPendingFocus(
			state,
			action: PayloadAction<{ chatUid: string; messageUid: string; modelSlug: string; requestId?: number }>
		) {
			state.pendingFocus = {
				chatUid: action.payload.chatUid,
				messageUid: action.payload.messageUid,
				modelSlug: action.payload.modelSlug,
				requestId: action.payload.requestId ?? Date.now(),
			}
		},
		clearPendingFocus(state) {
			state.pendingFocus = null
		},
		resetChatsState(state) {
			// Keep search focus across model-page remounts during navigation.
			return { ...initialState, pendingFocus: state.pendingFocus }
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchChatsByModel.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchChatsByModel.fulfilled, (state, action) => {
				state.loading = false
				state.chats = action.payload.chats
				const hasCurrentChat =
					state.currentChat != null && action.payload.chats.some((chat) => chat.uid === state.currentChat)
				state.currentChat = hasCurrentChat ? state.currentChat : action.payload.currentChat
			})
			.addCase(fetchChatsByModel.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message ?? 'Ошибка загрузки чатов'
			})

			.addCase(createChatForModel.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(createChatForModel.fulfilled, (state, action) => {
				state.loading = false
				if (state.chats) {
					state.chats = [...state.chats, action.payload]
				} else {
					state.chats = [action.payload]
				}
				state.currentChat = action.payload.uid
			})
			.addCase(createChatForModel.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message ?? 'Ошибка создания чата'
			})

			.addCase(deleteChatByUid.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(deleteChatByUid.fulfilled, (state, action) => {
				state.loading = false
				if (state.chats) {
					state.chats = state.chats.filter((c) => c.uid !== action.payload)
				}
				if (state.chats && state.chats.length > 0) {
					state.currentChat = state.chats[0].uid
				} else {
					state.currentChat = null
				}
			})
			.addCase(deleteChatByUid.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message ?? 'Ошибка удаления чата'
			})
            
			.addCase(renameChatByUid.pending, (state) => {
				state.error = null
			})
			.addCase(renameChatByUid.fulfilled, (state, action) => {
				if (state.chats) {
					state.chats = state.chats.map((c) => (c.uid === action.payload.uid ? { ...c, title: action.payload.title } : c))
				}
			})
			.addCase(renameChatByUid.rejected, (state, action) => {
				state.error = action.error.message ?? 'Ошибка переименования чата'
			})
	},
})

export const { setCurrentChat, resetChatsState, moveChatToTop, setPendingFocus, clearPendingFocus } = chatsSlice.actions
export const chatsReducer = chatsSlice.reducer

export const selectChats = (state: RootState) => state.chats.chats
export const selectCurrentChat = (state: RootState) => state.chats.currentChat
export const selectChatsLoading = (state: RootState) => state.chats.loading
export const selectChatsError = (state: RootState) => state.chats.error
export const selectPendingFocus = (state: RootState) => state.chats.pendingFocus


